import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Background,
  BackgroundVariant,
  Controls,
  MarkerType,
  MiniMap,
  ReactFlow,
  ReactFlowProvider,
  addEdge,
  applyEdgeChanges,
  applyNodeChanges,
  useReactFlow,
  type Connection,
  type Edge,
  type EdgeChange,
  type Node,
  type NodeChange,
  type OnConnect,
} from '@xyflow/react';
import {
  ArrowLeft,
  Hash,
  LayoutGrid,
  Loader2,
  Mic,
  PhoneIncoming,
  PhoneOff,
  Plus,
  RotateCcw,
  Save,
  Search,
  Users,
  Volume2,
} from 'lucide-react';
import dagre from 'dagre';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils/cn';
import { useAuthStore } from '@/lib/auth/store';
import { useThemeStore } from '@/lib/theme/store';
import {
  useUserGroupAgents,
  usePromptUploads,
} from '@/features/voice-campaigns/api';
import { useSmsWebhookList } from '@/features/webhooks/api';
import '@xyflow/react/dist/style.css';

import { IvrEditorContext } from '../context';
import { transformNodes, validateIVR } from '../transform';
import {
  DTMF_KEYS,
  IVR_SETTING_ORDER,
  VERB_COLOR,
  type IvrComposeRequest,
  type IvrEdge as IvrEdgeType,
  type IvrNode as IvrNodeType,
  type IvrNodeData,
  type IvrNodeType as IvrNodeKind,
  type IvrVerb,
} from '../types';

import { AnswerNode } from './nodes/AnswerNode';
import { HangupNode } from './nodes/HangupNode';
import { PlayNode } from './nodes/PlayNode';
import { DtmfNode } from './nodes/DtmfNode';
import { RecordNode } from './nodes/RecordNode';
import { TransferNode } from './nodes/TransferNode';
import { IvrEdge } from './edges/IvrEdge';
import { useIvrSettings } from '../api';

const nodeTypes = {
  answerNode: AnswerNode,
  playNode: PlayNode,
  gatherNode: DtmfNode,
  transferNode: TransferNode,
  recordNode: RecordNode,
  hangupNode: HangupNode,
};
const edgeTypes = { custom: IvrEdge };

type PaletteCategory = 'ACTION' | 'INPUT' | 'FORWARD' | 'RECORD';

interface PaletteItem {
  verb: IvrVerb;
  label: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  category: PaletteCategory;
  /** When true, can't be added more than once. */
  unique?: boolean;
}

const _PALETTE: PaletteItem[] = [
  {
    verb: 'answer',
    label: 'Answer',
    description: 'Accept the call',
    icon: PhoneIncoming,
    category: 'ACTION',
    unique: true,
  },
  {
    verb: 'play',
    label: 'Play',
    description: 'Play an audio prompt',
    icon: Volume2,
    category: 'ACTION',
  },
  {
    verb: 'hangup',
    label: 'Hangup',
    description: 'End the call',
    icon: PhoneOff,
    category: 'ACTION',
  },
  {
    verb: 'gather',
    label: 'DTMF Menu',
    description: 'Collect a digit choice',
    icon: Hash,
    category: 'INPUT',
  },
  {
    verb: 'transfer',
    label: 'Transfer',
    description: 'Patch to an agent group',
    icon: Users,
    category: 'FORWARD',
  },
  {
    verb: 'record',
    label: 'Record',
    description: 'Capture caller audio',
    icon: Mic,
    category: 'RECORD',
  },
];

const PALETTE_ORDER: PaletteCategory[] = ['ACTION', 'INPUT', 'FORWARD', 'RECORD'];

interface IvrFlowEditorProps {
  mode: 'create' | 'update';
  ivrId?: number;
  /** When true the editor is view-only — no sidebar, no save/tidy/reset. */
  readOnly?: boolean;
  /** Pre-loaded nodes / edges (for edit mode). */
  initialNodes?: IvrNodeType[];
  initialEdges?: IvrEdgeType[];
  initialFlowName?: string;
  initialIsGatherNode?: number;
  /** Mutation callbacks supplied by the wrapper page. */
  onSave: (req: IvrComposeRequest) => Promise<void> | void;
  isPending: boolean;
}

export function IvrFlowEditor(props: IvrFlowEditorProps) {
  return (
    <ReactFlowProvider>
      <IvrFlowEditorInner {...props} />
    </ReactFlowProvider>
  );
}

function IvrFlowEditorInner({
  mode,
  readOnly = false,
  initialNodes,
  initialEdges,
  initialFlowName,
  initialIsGatherNode,
  onSave,
  isPending,
}: IvrFlowEditorProps) {
  const userId = useAuthStore((s) => s.userId);
  const rf = useReactFlow();

  // Auto-collapse the app sidebar while the IVR editor is open — the canvas
  // needs every horizontal pixel. Restore whatever state the user had on
  // unmount so navigation away returns them to their normal layout.
  useEffect(() => {
    const themeStore = useThemeStore.getState();
    const prevCollapsed = themeStore.sidebarCollapsed;
    if (!prevCollapsed) themeStore.setSidebarCollapsed(true);
    return () => {
      if (!prevCollapsed) {
        useThemeStore.getState().setSidebarCollapsed(false);
      }
    };
  }, []);

  /* ---- Data sources ---- */
  const prompts = usePromptUploads();
  const agentGroups = useUserGroupAgents();
  const smsHooks = useSmsWebhookList();
  const ivrSettings = useIvrSettings();

  /* ---- Editor state ---- */
  const [flowName, setFlowName] = useState(initialFlowName ?? '');
  const [nameError, setNameError] = useState('');
  const [isGatherNode, setIsGatherNode] = useState(initialIsGatherNode ?? 0);

  const [nodes, setNodes] = useState<Node[]>(
    (initialNodes ?? []) as unknown as Node[],
  );
  const [edges, setEdges] = useState<Edge[]>(
    (initialEdges ?? []) as unknown as Edge[],
  );

  // Seed the canvas with an Answer node on first mount in create mode.
  // React 18 StrictMode runs effects twice — guard with a ref so we only
  // create one Answer node even when the effect double-fires.
  const seededRef = useRef(false);
  useEffect(() => {
    if (mode !== 'create') return;
    if (seededRef.current) return;
    if (nodes.length > 0) return;
    seededRef.current = true;
    addNode('answer');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ---- Derived ---- */
  const smsWebhookFlag = (smsHooks.data ?? []).length > 0;

  const nodeConnections = useMemo(() => {
    const out: Record<string, string[]> = {};
    for (const e of edges) {
      const key = e.sourceHandle ?? e.source;
      if (!out[key]) out[key] = [];
      out[key].push(e.target);
    }
    return out;
  }, [edges]);

  /* ---- React Flow change handlers ---- */
  const onNodesChange = useCallback(
    (changes: NodeChange[]) =>
      setNodes((nds) => applyNodeChanges(changes, nds)),
    [],
  );
  const onEdgesChange = useCallback(
    (changes: EdgeChange[]) =>
      setEdges((eds) => applyEdgeChanges(changes, eds)),
    [],
  );

  /* ---- Node ops ---- */
  const generateNodeId = () =>
    `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;

  const addNode = (
    verb: IvrVerb,
    dropPosition?: { x: number; y: number },
  ) => {
    // Answer is unique.
    if (verb === 'answer' && nodes.some((n) => n.type === 'answerNode')) {
      toast.error('Only one Answer node per IVR.');
      return;
    }

    const id = generateNodeId();
    // Legacy nuance: `gather` verb maps to type `gatherNode`, and the
    // verb stored on `data` is "dtmf".
    const type: IvrNodeKind = ((): IvrNodeKind => {
      switch (verb) {
        case 'answer':
          return 'answerNode';
        case 'play':
          return 'playNode';
        case 'gather':
          return 'gatherNode';
        case 'transfer':
          return 'transferNode';
        case 'record':
          return 'recordNode';
        case 'hangup':
          return 'hangupNode';
        case 'dtmf':
          return 'gatherNode';
      }
    })();

    // If the caller supplied a drop position (drag-and-drop), use it.
    // Otherwise cascade horizontally: each click lands ~80px to the
    // right of the last placement with a small Y jitter so overlaps are
    // visible. Tidy can be used to clean up later.
    const offsetX = nodes.length * 80;
    const offsetY = (nodes.length % 3) * 60;
    const position =
      dropPosition ??
      (nodes.length === 0
        ? { x: 20, y: 200 }
        : { x: 80 + offsetX, y: 160 + offsetY });

    const data: IvrNodeData = {
      label: `${verb} Node`,
      verb: verb === 'gather' ? 'dtmf' : verb,
      userId: userId ?? undefined,
      // Gather/DTMF node defaults: wait time starts at 1s (range 1-15),
      // re-prompt count stays blank until the user sets it (range 1-5).
      ...(verb === 'gather' ? { timeout: 1 } : {}),
    };

    if (verb === 'gather') {
      setIsGatherNode((c) => c + 1);
    }

    setNodes((prev) => [
      ...prev,
      {
        id,
        type,
        position,
        data,
      } as Node,
    ]);
  };

  /* ---- Drag & drop from the palette ---- */
  const onPaletteDragStart = (e: React.DragEvent, verb: IvrVerb) => {
    e.dataTransfer.setData('application/ivr-verb', verb);
    e.dataTransfer.effectAllowed = 'copy';
  };

  const onCanvasDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  }, []);

  const onCanvasDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const verb = e.dataTransfer.getData('application/ivr-verb') as IvrVerb;
      if (!verb) return;
      const position = rf.screenToFlowPosition({
        x: e.clientX,
        y: e.clientY,
      });
      addNode(verb, position);
    },
    // addNode reads `nodes` directly so eslint can't see the dep; the call
    // closes over current state at drop time, which is what we want.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rf],
  );

  const updateNodeData = useCallback(
    (nodeId: string, patch: Partial<IvrNodeData>) => {
      setNodes((nds) => {
        const prevNode = nds.find((n) => n.id === nodeId);
        const prevData = (prevNode?.data ?? {}) as IvrNodeData;

        // numDigits transitions kill stale edges (legacy behaviour).
        if (patch.numDigits !== undefined) {
          const prevDigits = Number(prevData.numDigits ?? 1);
          const nextDigits = Number(patch.numDigits);
          if (prevDigits !== nextDigits) {
            setEdges((eds) =>
              eds.filter((edge) => {
                if (edge.source !== nodeId) return true;
                if (nextDigits === 1) {
                  return edge.sourceHandle !== `${nodeId}_single_source`;
                }
                return !edge.sourceHandle?.startsWith(`${nodeId}_handle-`);
              }),
            );
          }
        }

        // Removed digit handles → drop the edges they fed.
        if (patch.selectedHandles && prevData.selectedHandles) {
          const removedHandles: string[] = [];
          for (const hid of Object.keys(prevData.selectedHandles)) {
            if (!Object.prototype.hasOwnProperty.call(patch.selectedHandles, hid)) {
              removedHandles.push(hid);
            }
          }
          if (removedHandles.length > 0) {
            setEdges((eds) =>
              eds.filter(
                (edge) =>
                  !(edge.source === nodeId &&
                    removedHandles.includes(edge.sourceHandle ?? '')),
              ),
            );
          }
        }

        return nds.map((node) =>
          node.id === nodeId
            ? { ...node, data: { ...(node.data as IvrNodeData), ...patch } }
            : node,
        );
      });
    },
    [],
  );

  const onRemove = useCallback((id: string) => {
    setNodes((nds) => nds.filter((n) => n.id !== id));
    setEdges((eds) => eds.filter((e) => e.source !== id && e.target !== id));
  }, []);

  const onRemoveEdge = useCallback((edgeId: string) => {
    setEdges((eds) => eds.filter((e) => e.id !== edgeId));
  }, []);

  const setParentForTarget = useCallback(
    (targetId: string, parentLabel: string, idToRemove?: string) => {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === targetId
            ? {
                ...n,
                data: {
                  ...(n.data as IvrNodeData),
                  parent: parentLabel,
                  idToRemove,
                },
              }
            : n,
        ),
      );
    },
    [],
  );

  const onConnect: OnConnect = useCallback(
    (connection: Connection) => {
      const { source, target, sourceHandle } = connection;
      if (!source || !target) return;
      const sourceNode = nodes.find((n) => n.id === source);
      if (!sourceNode) return;
      const sourceVerb = (sourceNode.data as IvrNodeData)?.verb;

      const baseEdge: Edge = {
        id: `e-${source}-${target}-${Date.now().toString(36)}`,
        source,
        target,
        sourceHandle: sourceHandle ?? null,
        targetHandle: connection.targetHandle ?? null,
        type: 'custom',
        markerEnd: { type: MarkerType.Arrow },
        data: { onRemove: onRemoveEdge },
      };

      if (sourceVerb === 'dtmf') {
        const numDigits = Number((sourceNode.data as IvrNodeData).numDigits ?? 1);
        if (numDigits === 1) {
          if ((nodeConnections[sourceHandle ?? ''] ?? []).length > 0) {
            toast.error('Each DTMF option can connect only once.');
            return;
          }
          const digit = sourceHandle?.split('-').pop() ?? '?';
          // Display-only label. The underlying `sourceHandle`
          // (`${id}_handle-${digit}`) and `flowControl.dtmf_${digit}` wire
          // keys are untouched — backend semantics unchanged.
          setEdges((eds) =>
            addEdge(
              {
                ...baseEdge,
                label: `Key ${digit}`,
                data: {
                  ...baseEdge.data,
                  label: `Key ${digit}`,
                  kind: 'dtmf',
                },
              },
              eds,
            ),
          );
          setParentForTarget(
            target,
            (sourceNode.data as IvrNodeData).label,
            `${sourceNode.id}_handle-${digit}`,
          );
          return;
        }
        if ((nodeConnections[source] ?? []).length >= 1) {
          toast.error('This node can only have one next step.');
          return;
        }
        setEdges((eds) => addEdge(baseEdge, eds));
        setParentForTarget(target, (sourceNode.data as IvrNodeData).label);
        return;
      }

      if (sourceVerb !== 'gather' && (nodeConnections[source] ?? []).length >= 1) {
        toast.error('A node can only have one child.');
        return;
      }
      setEdges((eds) => addEdge(baseEdge, eds));
      setParentForTarget(target, (sourceNode.data as IvrNodeData).label);
    },
    [nodes, nodeConnections, setParentForTarget, onRemoveEdge],
  );

  /* ---- Save ---- */
  const handleSave = async () => {
    if (nameError) {
      toast.error(nameError);
      return;
    }
    if (
      !validateIVR({
        nodes: nodes as unknown as IvrNodeType[],
        edges: edges as unknown as IvrEdgeType[],
        flowName,
      })
    ) {
      return;
    }
    const wire = transformNodes({
      nodesArray: nodes as unknown as IvrNodeType[],
      edges: edges as unknown as IvrEdgeType[],
      flowName,
      userId: Number(userId),
      isGatherNode,
      smsWebhookFlag,
    });
    const req: IvrComposeRequest = {
      nodeData: JSON.stringify(wire),
      edgesData: JSON.stringify(edges),
      flowName,
      userId: Number(userId),
      is_gather_node: isGatherNode,
      promptDuration: wire.promptDuration,
    };
    await onSave(req);
  };

  const handleReset = () => {
    /*
     * Build a fresh Answer node inline rather than calling `addNode`. The
     * `addNode` closure captures `nodes` at render time, so right after
     * `setNodes([])` it still sees the OLD answer node and trips the
     * `Only one Answer node per IVR` guard — leaving the canvas empty.
     * Seeding directly via `setNodes` avoids that race.
     */
    const answerNode: Node = {
      id: generateNodeId(),
      type: 'answerNode',
      position: { x: 20, y: 200 },
      data: {
        label: 'answer Node',
        verb: 'answer',
        userId: userId ?? undefined,
      } as IvrNodeData,
    } as Node;

    setNodes([answerNode]);
    setEdges([]);
    setFlowName('');
    setIsGatherNode(0);
    setNameError('');
    seededRef.current = true; // canvas already has the seed Answer node
  };

  /**
   * "Tidy" — dagre left-to-right auto-layout. Uses each node's measured
   * dimensions when available (so the layout matches the rendered size),
   * with a sensible fallback for nodes that haven't been measured yet.
   * After settling state, schedules a fitView so the camera reframes.
   */
  const handleTidy = useCallback(() => {
    if (nodes.length === 0) return;
    const g = new dagre.graphlib.Graph();
    g.setDefaultEdgeLabel(() => ({}));
    g.setGraph({
      rankdir: 'LR',
      nodesep: 30,
      ranksep: 60,
      marginx: 20,
      marginy: 20,
    });

    for (const n of nodes) {
      const measured =
        (n as { measured?: { width?: number; height?: number } }).measured ?? {};
      // Cards are 120px wide. Heights vary — small (~32) for Answer/Hangup
      // and taller for DTMF with stacked digit handles.
      const width = measured.width ?? 120;
      const height = measured.height ?? 40;
      g.setNode(n.id, { width, height });
    }
    for (const e of edges) {
      g.setEdge(e.source, e.target);
    }
    dagre.layout(g);

    setNodes((nds) =>
      nds.map((n) => {
        const pos = g.node(n.id);
        if (!pos) return n;
        // dagre positions are CENTER points; RF positions are TOP-LEFT.
        return {
          ...n,
          position: { x: pos.x - pos.width / 2, y: pos.y - pos.height / 2 },
        };
      }),
    );

    // Reframe the camera after RF processes the new positions.
    requestAnimationFrame(() => {
      rf.fitView({ padding: 0.15, duration: 300 });
    });
    toast.success('Layout tidied up');
  }, [nodes, edges, rf]);

  /* ---- Render ---- */
  const [paletteSearch, setPaletteSearch] = useState('');

  // Filter palette items by the search query — match on label or description.
  const paletteItems = useMemo<PaletteItem[]>(() => {
  if (!ivrSettings.data) return [];

  return ivrSettings.data.map((item) => ({
    verb: item.action as IvrVerb,
    label: item.name,
    description: item.name,
    unique: item.action === 'answer',

    category:
      item.action === 'answer' ||
      item.action === 'play' ||
      item.action === 'hangup'
        ? 'ACTION'
        : item.action === 'gather'
        ? 'INPUT'
        : item.action === 'transfer'
        ? 'FORWARD'
        : 'RECORD',

    icon:
      item.action === 'answer'
        ? PhoneIncoming
        : item.action === 'play'
        ? Volume2
        : item.action === 'hangup'
        ? PhoneOff
        : item.action === 'gather'
        ? Hash
        : item.action === 'transfer'
        ? Users
        : Mic,
  }));
}, [ivrSettings.data]);

const filteredPalette = useMemo(() => {
  const q = paletteSearch.trim().toLowerCase();

  if (!q) return paletteItems;

  return paletteItems.filter(
    (p) =>
      p.label.toLowerCase().includes(q) ||
      p.description.toLowerCase().includes(q),
  );
}, [paletteSearch, paletteItems]);

  return (
    <IvrEditorContext.Provider
      value={{
        readOnly,
        prompts: prompts.data ?? [],
        agentGroups: agentGroups.data ?? [],
        smsWebhookFlag,
        smsWebhookList: smsHooks.data ?? [],
        edges: edges as unknown as IvrEdgeType[],
        nodeConnections,
        updateNodeData,
        onRemove,
        getNode: (id) => nodes.find((n) => n.id === id) as IvrNodeType | undefined,
      }}
    >
      <div className="flex h-full min-h-0 flex-1 flex-col">
        {/* Slim top toolbar */}
        <div className="mb-3 flex items-center gap-3 rounded-md border border-border bg-card px-3 py-2 shadow-sm">
          <Button asChild variant="ghost" size="icon" className="h-7 w-7">
            <Link to="/ivr-list" aria-label="Back to IVRs">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>

          <div className="flex flex-1 items-center gap-2">
            <Input
              value={flowName}
              readOnly={readOnly}
              onChange={(e) => {
                if (readOnly) return;
                const cleaned = e.target.value.replace(/[^a-zA-Z0-9_]/g, '');
                setNameError(cleaned !== e.target.value ? 'Only letters, digits, and _' : '');
                setFlowName(cleaned);
              }}
              placeholder="IVR name (letters, digits, _)"
              className="h-8 max-w-sm border-transparent bg-transparent px-2 text-sm font-semibold shadow-none focus-visible:border-input focus-visible:bg-card"
            />
            <span className={cn(
              'rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
              readOnly
                ? 'bg-blue-100 text-blue-900'
                : 'bg-amber-100 text-amber-900',
            )}>
              {readOnly ? 'View Only' : mode === 'create' ? 'New' : 'Draft'}
            </span>
            {nameError && (
              <span className="text-xs text-destructive">{nameError}</span>
            )}
          </div>

          <div className="flex items-center gap-1">
            {/* Tidy just re-lays-out nodes on screen — it doesn't change
             * any saved data, so it stays available in View Only mode too. */}
            <Button
              variant="outline"
              size="sm"
              onClick={handleTidy}
              disabled={nodes.length === 0}
              title="Auto-arrange (Tidy)"
            >
              <LayoutGrid className="h-3.5 w-3.5" />
              Tidy
            </Button>
            {!readOnly && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleReset}
                  title="Reset the canvas"
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                  Reset
                </Button>
                <Button onClick={handleSave} disabled={isPending} size="sm">
                  {isPending ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Save className="h-3.5 w-3.5" />
                  )}
                  Save
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Layout: left palette + canvas */}
        <div className="flex min-h-0 flex-1 gap-3">
          {/* Left palette sidebar — flat 2-column widget grid with search.
              Each tile is click-to-add AND drag-to-canvas. */}
          {!readOnly && (
          <aside className="flex w-[240px] shrink-0 flex-col overflow-hidden rounded-lg border border-border bg-card shadow-sm">
            {/* Header */}
            <div className="border-b border-border px-4 py-3">
              <div className="text-sm font-bold tracking-tight text-foreground">Widget Library</div>
              <p className="mt-0.5 text-[11px] text-muted-foreground">
                Drop the widgets into the builder
              </p>
            </div>

            {/* Search */}
            <div className="border-b border-border px-3 py-3">
              <div className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={paletteSearch}
                  onChange={(e) => setPaletteSearch(e.target.value)}
                  placeholder="Search widgets"
                  className="h-8 rounded-md border-border bg-muted/40 pl-7 text-xs"
                />
              </div>
            </div>

            {/* Widget grid */}
            <div className="flex-1 overflow-y-auto px-3 py-3">
              {ivrSettings.isLoading ? (
                <div className="py-8 text-center text-xs">Loading...</div>
              ) : filteredPalette.length === 0 ? (
                <div className="py-8 text-center text-xs text-muted-foreground">
                  No widgets match
                  <div className="font-mono text-[10px]">"{paletteSearch}"</div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  {filteredPalette.map((item) => {
                    const Icon = item.icon;
                    const disabled = 
                    item.unique && 
                    nodes.some((n) => n.type === 'answerNode');
                    const v = VERB_COLOR[item.verb];
                    return (
                      <button
                        key={item.verb}
                        type="button"
                        disabled={disabled}
                        draggable={!disabled}
                        onClick={() => !disabled && addNode(item.verb)}
                        onDragStart={(e) => !disabled && onPaletteDragStart(e, item.verb)}
                        title={item.description}
                        className={cn(
                          'group/widget flex flex-col items-center gap-1.5 rounded-lg border border-transparent bg-transparent px-1 py-2.5 text-center transition-all',
                          disabled
                            ? 'cursor-not-allowed opacity-40'
                            : 'cursor-grab hover:-translate-y-px hover:border-border hover:bg-muted/40 hover:shadow-sm active:cursor-grabbing',
                        )}
                      >
                        {/* Circular icon tile — vibrant icon on tinted bg */}
                        <span
                          className={cn(
                            'flex h-11 w-11 items-center justify-center rounded-full shadow-sm ring-1 ring-border transition-shadow group-hover/widget:shadow-md',
                            v.bgSoft,
                          )}
                        >
                          <Icon className={cn('h-5 w-5', v.fgStrong)} />
                        </span>
                        <span className="text-[10.5px] font-medium leading-tight text-foreground">
                          {item.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer counter */}
            <div className="border-t border-border px-4 py-2 text-[10px] text-muted-foreground">
              {nodes.length} nodes · {edges.length} edges
            </div>
          </aside>
          )}

          {/* Canvas — also the drop target for palette widgets. */}
          <div
            className="relative flex min-h-0 flex-1 overflow-hidden rounded-md border border-border bg-card"
            {...(readOnly ? {} : { onDragOver: onCanvasDragOver, onDrop: onCanvasDrop })}
          >
            <ReactFlow
              nodes={nodes}
              edges={edges.map((e) => ({
                ...e,
                data: { ...e.data, onRemove: readOnly ? undefined : onRemoveEdge },
              }))}
              onConnect={readOnly ? undefined : onConnect}
              // Node position/drag changes must always go through, even in
              // View Only, so nodes can still be moved around for layout.
              // Connecting/disconnecting edges stays blocked via onConnect
              // and onEdgesChange above/below.
              onNodesChange={onNodesChange}
              onEdgesChange={readOnly ? undefined : onEdgesChange}
              nodeTypes={nodeTypes}
              edgeTypes={edgeTypes}
              nodesDraggable
              nodesConnectable={!readOnly}
              elementsSelectable={!readOnly}
              fitView
              proOptions={{ hideAttribution: true }}
            >
              <Background variant={BackgroundVariant.Dots} gap={18} size={1} />
              <Controls />
              <MiniMap pannable zoomable className="!bg-card" />
            </ReactFlow>
          </div>
        </div>
      </div>
    </IvrEditorContext.Provider>
  );
}

// Silence unused-import warnings for legacy reference exports.
void DTMF_KEYS;
void IVR_SETTING_ORDER;
void Label;
void Plus;
void PALETTE_ORDER;
void _PALETTE;